import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@elysian/ui';
import type { InvitationPreview } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { AuthLayout } from './AuthLayout';

const ROLE: Record<InvitationPreview['role'], string> = {
  owner: 'an owner', admin: 'an admin', bookkeeper: 'a bookkeeper', accountant: 'their accountant', viewer: 'a viewer (read only)',
};

/** The link in an invitation email: /invitations/accept?token=… */
export function AcceptInvite() {
  const { source, status, user, chooseOrg, refresh, signOut } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const here = `/invitations/accept?token=${encodeURIComponent(token)}`;
  const preview = useAsync(() => (token ? source.invitationPreview(token) : Promise.reject(new Error('This link is missing its invitation code. Open the link from the email again.'))), [source, token]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (preview.state === 'loading') return <AuthLayout title="Invitation"><p className="el-muted" style={{ margin: 0 }}>Checking the invitation…</p></AuthLayout>;
  if (preview.state === 'error') {
    return (
      <AuthLayout title="Invitation">
        <div className="el-error-box" role="alert">{preview.error.message}</div>
        <p style={{ margin: 0 }}><Link to="/">Go to the Ledger</Link></p>
      </AuthLayout>
    );
  }
  const p = preview.data;
  const signedIn = status !== 'signed_out' && status !== 'starting';

  async function accept() {
    setBusy(true);
    setError(null);
    try {
      const org = await source.acceptInvitation(token);
      await refresh();
      if (useSession.getState().orgId !== org.id) await chooseOrg(org.id);
      navigate('/', { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <AuthLayout title={`Join ${p.organisation}`}>
      {p.status === 'pending' ? (
        <>
          <p style={{ margin: 0 }}>
            You’ve been invited to {p.organisation}’s books as {ROLE[p.role]}{p.firm ? ` (${p.firm})` : ''}. The invitation was sent to <strong>{p.email_hint}</strong>;
            sign in with that address to accept it.
          </p>
          {signedIn ? (
            <>
              <Button variant="primary" disabled={busy} onClick={() => void accept()}>Accept and open the books</Button>
              <p className="el-hint" style={{ margin: 0 }}>
                Signed in as {user.email ?? user.name}. Not the right account?{' '}
                <button type="button" className="el-linkbtn" onClick={() => void signOut().then(() => navigate(`/sign-in?returnTo=${encodeURIComponent(here)}`))}>Sign in as someone else</button>
              </p>
            </>
          ) : (
            <Button variant="primary" onClick={() => navigate(`/sign-in?returnTo=${encodeURIComponent(here)}`)}>Sign in to accept</Button>
          )}
        </>
      ) : (
        <p style={{ margin: 0 }}>
          {p.status === 'accepted' ? 'This invitation has already been used.' : p.status === 'expired' ? 'This invitation has expired.' : 'This invitation was withdrawn.'}{' '}
          Ask {p.organisation} to send a new one if you still need access.
        </p>
      )}
      {error && <div className="el-error-box" role="alert">{error}</div>}
    </AuthLayout>
  );
}
